import { Card } from '@/components/ui/card'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useGetSystemSettingsQuery, useUpdateSystemSettingsMutation } from '@/services/systemConfigApi'
import { useGetIncomeCategoryQuery } from '@/services/incomeCategoryApi'
import { toast } from '@/components/ui/use-toast'
import { Button } from '@/components/ui/button'
import { Edit2, Save, X } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  ConfigurationForm,
  DHAccountingFormData,
  transformFormDataToPayload,
  IncomeCategoryMappingRow
} from './configuration-form'
import { ERPLogsTable } from './erp-logs-table'
import useDocumentTitle from '@/components/ui/common/document-title'
import { useDebounce } from '@/hooks/use-debounce'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'

// Helper function to safely get property value as string
const getSafeProperty = (obj: Record<string, string> | {} | undefined, key: string): string => {
  if (obj && typeof obj === 'object' && key in obj) {
    const value = (obj as Record<string, string>)[key]
    return value?.toString() || ''
  }
  return ''
}

// Parse API fields that may be JSON strings using single quotes
const parseJsonish = (value: unknown): any => {
  if (typeof value !== 'string') return value
  // First try normal JSON.parse (in case backend already sends valid JSON)
  try {
    return JSON.parse(value)
  } catch { }
  // Fallback: convert single quotes to double quotes and try again
  try {
    const sanitized = value.replace(/'\s*:\s*'/g, '":"').replace(/'/g, '"')
    return JSON.parse(sanitized)
  } catch {
    return {}
  }
}

const hasRevenueData = (raw: unknown): boolean => {
  const obj = parseJsonish(raw) || {}
  if (!obj || typeof obj !== 'object') return false
  const { Comp, BAHKey, party, PartyCode, SysTag, pkey1, categories } = obj as Record<string, unknown>
  const hasTopLevel = [Comp, BAHKey, party, PartyCode, SysTag, pkey1].some(v => {
    const s = (v ?? '').toString().trim()
    return s.length > 0
  })
  const hasCategories = categories && typeof categories === 'object' && Object.keys(categories as object).length > 0
  return Boolean(hasTopLevel || hasCategories)
}

const DHAccounting = () => {
  useDocumentTitle("DH Accounting")
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const [revenueType, setRevenueType] = useState<'donation' | 'income'>('donation')
  const [isEditMode, setIsEditMode] = useState(false)
  const [activeTab, setActiveTab] = useState('configuration')
  const [hasBudgetDataError, setHasBudgetDataError] = useState(false)

  // Store form data for each revenue type to prevent data loss when switching
  const [donationFormData, setDonationFormData] = useState<DHAccountingFormData | null>(null)
  const [incomeFormData, setIncomeFormData] = useState<DHAccountingFormData | null>(null)

  // Use refs to track latest saved data to avoid stale state issues when switching quickly
  const donationFormDataRef = useRef<DHAccountingFormData | null>(null)
  const incomeFormDataRef = useRef<DHAccountingFormData | null>(null)
  const pendingResetDataRef = useRef<DHAccountingFormData | null>(null)

  // Keep refs in sync with state
  useEffect(() => {
    donationFormDataRef.current = donationFormData
  }, [donationFormData])

  useEffect(() => {
    incomeFormDataRef.current = incomeFormData
  }, [incomeFormData])

  // Default values for form initialization
  const getDefaultValues = (): DHAccountingFormData => ({
    dh_accounting: {
      api_url: '',
      organization_id: '',
      year: new Date().getFullYear().toString(),
      bh_type: '',
      income: {
        Comp: '',
        BAHKey: '',
        party: undefined,
        PartyCode: '',
        SysTag: undefined,
        pkey1: '',
        categories: [
          { income_category_id: '', account_budget: '', account_title: '', budkey: '' }
        ]
      },
      donation: {
        Comp: '',
        BAHKey: '',
        party: undefined,
        PartyCode: '',
        SysTag: undefined,
        pkey1: '',
        categories: [
          { income_category_id: '', account_budget: '', account_title: '', budkey: '' }
        ]
      }
    }
  })

  // Fetch current settings
  const { data: systemSettings, isLoading: isLoadingSettings } = useGetSystemSettingsQuery({
    query: 'groups=dh_accounting'
  })

  const [updateSettings, { isLoading: isUpdating }] = useUpdateSystemSettingsMutation()

  const form = useForm<DHAccountingFormData>({
    mode: "onChange",
    defaultValues: getDefaultValues()
  })


  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isLoading }
  } = form
  const watchedOrgId = watch('dh_accounting.organization_id')
  const debouncedOrgId = useDebounce(watchedOrgId, 500)

  // Handle form reset when revenue type changes (after state update)
  useEffect(() => {
    if (pendingResetDataRef.current) {
      reset(pendingResetDataRef.current, { keepValues: false })
      pendingResetDataRef.current = null
    }
  }, [revenueType, reset])

  // Stable callback for budget data error handler
  const handleBudgetDataError = useCallback((hasError: boolean) => {
    setHasBudgetDataError(hasError)
  }, [])

  // Fetch income categories for both revenue types to get category names for payload
  const { data: donationIncomeCategories } = useGetIncomeCategoryQuery(
    { org_id: orgId || 0, query: '', revenue_type: 'donation' },
    { skip: !debouncedOrgId || debouncedOrgId.trim() === '' }
  )
  const { data: incomeIncomeCategories } = useGetIncomeCategoryQuery(
    { org_id: orgId || 0, query: '', revenue_type: 'income' },
    { skip: !debouncedOrgId || debouncedOrgId.trim() === '' }
  )

  // Combine all income categories for name mapping
  const allIncomeCategories = useMemo(() => {
    const donation = Array.isArray(donationIncomeCategories?.data) ? donationIncomeCategories.data : []
    const income = Array.isArray(incomeIncomeCategories?.data) ? incomeIncomeCategories.data : []
    return [...donation, ...income]
  }, [donationIncomeCategories, incomeIncomeCategories])


  // Helper function to check if settings are empty/don't exist
  const areSettingsEmpty = (settings: any): boolean => {
    if (!settings) return true

    const dhAccounting = settings.dh_accounting

    // Check if dh_accounting exists and has api_url or organization_id
    const hasApiUrl = dhAccounting && (getSafeProperty(dhAccounting, 'api_url') || getSafeProperty(dhAccounting, 'accounting_url'))
    const hasOrgId = dhAccounting && getSafeProperty(dhAccounting, 'organization_id')

    // Check if income or donation sections have meaningful data (handle string payloads)
    const hasIncomeData = dhAccounting?.income ? hasRevenueData(dhAccounting.income) : false
    const hasDonationData = dhAccounting?.donation ? hasRevenueData(dhAccounting.donation) : false

    return !hasApiUrl && !hasOrgId && !hasIncomeData && !hasDonationData
  }

  // Build complete form data from system settings (for initial load only)
  const buildFormDataFromSystemSettings = (): DHAccountingFormData => {
    const dhAccounting = systemSettings ? (systemSettings as any).dh_accounting : null

    if (!dhAccounting) {
      return getDefaultValues()
    }

    const sharedFields = {
      api_url: getSafeProperty(dhAccounting, 'api_url') || getSafeProperty(dhAccounting, 'accounting_url'),
      organization_id: getSafeProperty(dhAccounting, 'organization_id'),
      year: getSafeProperty(dhAccounting, 'year') || new Date().getFullYear().toString(),
      bh_type: getSafeProperty(dhAccounting, 'bh_type')
    }

    const buildRevenueData = (type: 'donation' | 'income') => {
      const raw = dhAccounting[type]
      if (!raw) return getDefaultValues().dh_accounting[type]

      const data = parseJsonish(raw) || {}
      const categories = (data.categories && typeof data.categories === 'object') ? data.categories : {}

      return {
        Comp: getSafeProperty(data, 'Comp'),
        BAHKey: getSafeProperty(data, 'BAHKey'),
        party: (() => {
          const partyValue = getSafeProperty(data, 'party')
          if (!partyValue) return undefined
          const numValue = Number(partyValue)
          return isNaN(numValue) ? undefined : numValue
        })(),
        PartyCode: getSafeProperty(data, 'PartyCode'),
        SysTag: (() => {
          const sysTagValue = getSafeProperty(data, 'SysTag')
          if (!sysTagValue) return undefined
          const numValue = Number(sysTagValue)
          return isNaN(numValue) ? undefined : numValue
        })(),
        pkey1: getSafeProperty(data, 'pkey1'),
        categories: (Object.keys(categories).length > 0)
          ? Object.entries(categories).map(([categoryId, categoryData]: [string, any]) => {
            // The key is already the income_category_id (numeric id as string)
            return {
              income_category_id: categoryId, // Use the key directly as income_category_id
              account_budget: getSafeProperty(categoryData, 'AHKey'),
              // Prefer AHTitle when available; fallback to category_name
              account_title: getSafeProperty(categoryData, 'AHTitle') || getSafeProperty(categoryData, 'category_name'),
              budkey: getSafeProperty(categoryData, 'Budkey')
            }
          })
          : [{ income_category_id: '', account_budget: '', account_title: '', budkey: '' }]
      }
    }

    return {
      dh_accounting: {
        ...sharedFields,
        donation: buildRevenueData('donation'),
        income: buildRevenueData('income')
      }
    }
  }

  // Initialize form with data on mount only
  useEffect(() => {
    if (systemSettings && !donationFormData && !incomeFormData) {
      // Build form data from system settings
      const initialData = buildFormDataFromSystemSettings()

      // Check if settings are empty and enable edit mode if they are
      if (areSettingsEmpty(systemSettings)) {
        setIsEditMode(true)
      }

      // Initialize both saved states with the complete data
      setDonationFormData(initialData)
      setIncomeFormData(initialData)
      donationFormDataRef.current = initialData
      incomeFormDataRef.current = initialData

      // Reset form with initial data
      reset(initialData)
    }
  }, [systemSettings]) // Only depend on systemSettings

  // Initialize income category mappings when form loads or edit mode is enabled
  useEffect(() => {
    if (isEditMode) {
      const currentCategories = watch(`dh_accounting.${revenueType}.categories`)
      if (!currentCategories || currentCategories.length === 0) {
        setValue(`dh_accounting.${revenueType}.categories`, [{ income_category_id: '', account_budget: '', account_title: '', budkey: '' }])
      }
    }
  }, [isEditMode, setValue, watch, revenueType])

  const handleRevenueTypeChange = (type: 'donation' | 'income') => {
    // Don't do anything if already on the same type
    if (revenueType === type) return

    // Save current form data before switching - use getValues() to get all current form values
    // This captures any unsaved changes in the current form
    const currentData = form.getValues()

    // Get the latest saved data from refs (always up-to-date, avoids stale state)
    const latestDonationData = donationFormDataRef.current
    const latestIncomeData = incomeFormDataRef.current

    // Build the complete form data with correct sections:
    // - Save the current revenue type's section from current form (capture all edits)
    // - Preserve the other revenue type's section from saved state (the one we're switching to)
    const completeFormData: DHAccountingFormData = {
      dh_accounting: {
        api_url: currentData.dh_accounting.api_url || '',
        organization_id: currentData.dh_accounting.organization_id || '',
        year: currentData.dh_accounting.year || new Date().getFullYear().toString(),
        bh_type: currentData.dh_accounting.bh_type || '',
        // If we're currently on donation, save current donation section; otherwise use saved donation section
        donation: revenueType === 'donation'
          ? currentData.dh_accounting.donation
          : (latestDonationData?.dh_accounting?.donation || getDefaultValues().dh_accounting.donation),
        // If we're currently on income, save current income section; otherwise use saved income section
        income: revenueType === 'income'
          ? currentData.dh_accounting.income
          : (latestIncomeData?.dh_accounting?.income || getDefaultValues().dh_accounting.income)
      }
    }

    // Update both saved states with the complete form data (they both contain the same complete data)
    setDonationFormData(completeFormData)
    setIncomeFormData(completeFormData)
    donationFormDataRef.current = completeFormData
    incomeFormDataRef.current = completeFormData

    // Store the data to reset with after revenueType state update
    pendingResetDataRef.current = completeFormData

    // Switch to new revenue type - useEffect will handle the reset
    setRevenueType(type)
  }


  const handleEditMode = () => {
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    // Reset form to the saved data for current revenue type
    // Use refs to get the latest saved data
    const savedData = revenueType === 'donation'
      ? (donationFormDataRef.current || donationFormData)
      : (incomeFormDataRef.current || incomeFormData)
    if (savedData) {
      reset(savedData)
    }
  }

  const onSubmit = async (data: DHAccountingFormData) => {
    try {
      // Validate required fields
      if (!data.dh_accounting.organization_id || data.dh_accounting.organization_id.trim() === '') {
        toast({
          variant: 'destructive',
          description: 'Organization ID is required'
        })
        return
      }

      if (!data.dh_accounting.year || data.dh_accounting.year.trim() === '') {
        toast({
          variant: 'destructive',
          description: 'Year is required'
        })
        return
      }

      // Transform form data to the payload structure (convert categories array to object)
      // Pass income categories to get category names for category_name field
      const payload = transformFormDataToPayload(data, allIncomeCategories)

      await updateSettings(payload).unwrap()
      toast({
        variant: 'success',
        description: 'DH accounting settings updated successfully!'
      })

      // Update both saved states with the complete data to ensure consistency
      setDonationFormData(data)
      setIncomeFormData(data)
      donationFormDataRef.current = data
      incomeFormDataRef.current = data

      setIsEditMode(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        description: 'Failed to update settings. Please try again.'
      })
      console.error('Update settings error:', error)
    }
  }

  if (isLoadingSettings) {
    return (
      <div className="w-full p-4">
        <Card className="p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm">Loading settings...</p>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full p-4">
      <Card className="p-4">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">DH Accounting System</h2>
            <p className="text-sm text-gray-600">
              Configure accounting integration and monitor invoice synchronization activity.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-fit mb-4" variant="underline">
            <TabsTrigger value="configuration" variant="underline" className="flex-1">
              Configuration
            </TabsTrigger>
            <TabsTrigger value="logs" variant="underline" className="flex-1">
              Activity
            </TabsTrigger>
          </TabsList>

          {/* Configuration Tab Content */}
          <TabsContent value="configuration" className="mt-0">
            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Header Bar with Action Buttons */}
              <div className="flex items-center justify-between gap-4 pb-3 border-b">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    System Configuration
                  </h3>
                  {!isEditMode && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Read-only mode
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  {!isEditMode ? (
                    <Button
                      onClick={handleEditMode}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      <Edit2 className="h-3 w-3" />
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading || hasBudgetDataError || !form.formState.isValid || isUpdating}
                        size="sm"
                        className="flex items-center gap-1"
                        title={!form.formState.isValid ? 'Please fill all required fields before saving' : ''}
                      >
                        <Save className="h-3 w-3" />
                        {isUpdating ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <ConfigurationForm
                form={form}
                revenueType={revenueType}
                isEditMode={isEditMode}
                currentFormData={(revenueType === 'donation'
                  ? (donationFormDataRef.current || donationFormData)
                  : (incomeFormDataRef.current || incomeFormData)) || getDefaultValues()}
                onRevenueTypeChange={handleRevenueTypeChange}
                setValue={setValue}
                watchedOrgId={watchedOrgId}
                onBudgetDataError={handleBudgetDataError}
              />
            </form>
          </TabsContent>

          {/* Activity Tab Content */}
          <TabsContent value="logs" className="mt-0">
            <ERPLogsTable />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  )
}

export default DHAccounting